import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { ApiError } from "./api";
import { ResourceCache } from "./resourceCache";

/** Owned by Shell, below IdentityScope. Standalone components get a private cache. */
export const ResourceCacheContext = createContext<ResourceCache | null>(null);

export function useResourceCache() {
  const shared = useContext(ResourceCacheContext);
  const [local] = useState(() => new ResourceCache());
  return shared ?? local;
}

export interface Resource<T> {
  data: T | null;
  failure: ApiError | null;
  loading: boolean;
  refreshFailure: ApiError | null;
}
export interface UseResourceOptions {
  /** Only for a refinement of the same workspace, never a different record. */
  keepPreviousDataOnNavigation?: boolean;
}

function workspace(key: string | null) {
  return new URLSearchParams(key?.split("?")[1] ?? "").get("workspace");
}
const empty = <T,>(loading: boolean): Resource<T> => ({ data: null, failure: null, loading, refreshFailure: null });

/** Cached reads paint synchronously; every navigation revalidates. Only the
 * current key/load may publish, except that a read a soft refresh superseded
 * still paints its snapshot if nothing newer has: otherwise refreshes arriving
 * faster than a slow read answers would never let the page leave its skeleton.
 * Refresh failures retain data except auth/not-found. */
export function useResource<T>(
  key: string | null,
  fetcher: () => Promise<T>,
  refreshKey = 0,
  options: UseResourceOptions = {},
): Resource<T> {
  const cache = useResourceCache();
  const epoch = useSyncExternalStore(cache.subscribe, cache.version, cache.version);
  const [state, setState] = useState<{ key: string | null; purge: number; value: Resource<T> }>(() => ({
    key, purge: cache.purgeVersion(), value: { ...empty<T>(key !== null), data: key === null ? null : cache.get<T>(key) },
  }));
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const previousRefresh = useRef(refreshKey);
  const purged = state.purge !== cache.purgeVersion();
  const keepPrevious = !purged && options.keepPreviousDataOnNavigation && workspace(state.key) === workspace(key);
  // Effects run after paint: never return another key's records for that frame.
  const visible: Resource<T> = key === null ? empty(false) : purged ? empty<T>(true) : state.key === key ? state.value : {
    ...empty<T>(true), data: cache.get<T>(key) ?? (keepPrevious ? state.value.data : null),
  };
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const activeKey = useRef(key);
  activeKey.current = key;
  const issued = useRef(0);
  const painted = useRef(0);

  useEffect(() => {
    if (key === null) {
      setState({ key, purge: cache.purgeVersion(), value: empty(false) });
      return;
    }
    let current = true;
    const seq = ++issued.current;
    const purge = cache.purgeVersion();
    const supersede = previousRefresh.current !== refreshKey;
    previousRefresh.current = refreshKey;
    const data = visibleRef.current.data;
    setState({ key, purge: cache.purgeVersion(), value: { data, loading: true, failure: null, refreshFailure: null } });
    const read = fetcherRef.current;
    void cache.load(key, read, supersede ? refreshKey : undefined).then(next => {
      if (current && cache.version() === epoch) {
        painted.current = seq;
        setState({ key, purge, value: { data: next, loading: false, failure: null, refreshFailure: null } });
      } else if (!current && activeKey.current === key && cache.purgeVersion() === purge && seq > painted.current) {
        // Superseded by a soft refresh that is still in flight: paint this snapshot meanwhile.
        painted.current = seq;
        setState(previous => previous.key === key ? { key, purge, value: { data: next, loading: true, failure: null, refreshFailure: null } } : previous);
      }
    }).catch((error: unknown) => {
      if (!current || cache.version() !== epoch) return;
      const failure = error instanceof ApiError ? error : new ApiError(0, (error as Error)?.message || "Request failed");
      const denied = [401, 403, 404].includes(failure.status);
      // Do not notify/refetch on a denial: that would turn it into a request loop.
      if (denied) cache.clear();
      setState({ key, purge: cache.purgeVersion(), value: data !== null && !denied
        ? { data, loading: false, failure: null, refreshFailure: failure }
        : { data: null, loading: false, failure, refreshFailure: null } });
    });
    return () => { current = false; };
  }, [key, refreshKey, epoch, cache]);

  return visible;
}

/** Reports may need slower provider reads while unrelated rows stream in.
 * Finish one read and queue one follow-up on soft refreshes. Identity changes
 * and security purges still discard every old response before it can paint. */
export function useCoalescedResource<T>(key: string | null, fetcher: () => Promise<T>, refreshKey = 0): Resource<T> {
  const cache = useResourceCache();
  const epoch = useSyncExternalStore(cache.subscribe, cache.version, cache.version);
  const purge = cache.purgeVersion();
  const readRef = useRef(fetcher); readRef.current = fetcher;
  const refreshRef = useRef<() => void>(() => {});
  const [state, setState] = useState<{ key: string | null; purge: number; value: Resource<T> }>(() => ({
    key, purge, value: { ...empty<T>(key !== null), data: key === null ? null : cache.get<T>(key) },
  }));
  const observed = useRef({ epoch, refreshKey });
  useEffect(() => {
    if (key === null) { refreshRef.current = () => {}; return; }
    let disposed = false, running = false, queued = false;
    let data = cache.get<T>(key);
    const publish = (value: Resource<T>) => {
      if (!disposed && cache.purgeVersion() === purge) setState({ key, purge, value });
    };
    const run = async () => {
      if (disposed) return;
      if (running) { queued = true; return; }
      running = true;
      const version = cache.version();
      publish({ data, loading: true, failure: null, refreshFailure: null });
      try {
        const next = await readRef.current();
        if (disposed || cache.purgeVersion() !== purge) return;
        data = next;
        // A soft refresh may have arrived. Paint this completed snapshot with
        // its refresh state, but never replace a newer shared cached value.
        if (cache.version() === version) cache.set(key, next);
        publish({ data, loading: queued, failure: null, refreshFailure: null });
      } catch (error) {
        if (disposed || cache.purgeVersion() !== purge) return;
        const failure = error instanceof ApiError ? error : new ApiError(0, (error as Error)?.message || "Request failed");
        const denied = [401, 403, 404].includes(failure.status);
        if (denied) { data = null; queued = false; cache.clear(); }
        publish(data !== null ? { data, loading: false, failure: null, refreshFailure: failure }
          : { data: null, loading: false, failure, refreshFailure: null });
      } finally {
        running = false;
        if (queued && !disposed && cache.purgeVersion() === purge) { queued = false; void run(); }
      }
    };
    refreshRef.current = () => { void run(); };
    observed.current = { epoch: cache.version(), refreshKey };
    void run();
    return () => { disposed = true; refreshRef.current = () => {}; };
  }, [key, purge, cache]);
  useEffect(() => {
    const previous = observed.current; observed.current = { epoch, refreshKey };
    if (previous.epoch !== epoch || previous.refreshKey !== refreshKey) refreshRef.current();
  }, [epoch, refreshKey]);
  if (key === null) return empty(false);
  if (state.key !== key || state.purge !== purge) return { ...empty<T>(true), data: cache.get<T>(key) };
  return state.value;
}
